import './App.css';
import { useEffect, useState, useCallback } from "react";

function ResaAnalyse({ token }) {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL;

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch Reservations
            const resReservations = await fetch(`${API_URL}/reservation`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const reservations = await resReservations.json();

            // Fetch Business Expenses
            const resExpenses = await fetch(`${API_URL}/business_expense`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const expenses = await resExpenses.json();

            // Aggregate Data
            const monthlyData = {};

            // Process Reservations
            reservations.forEach(r => {
                if (!r.start_date) return;
                const date = new Date(r.start_date);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!monthlyData[monthKey]) {
                    monthlyData[monthKey] = {
                        month: monthKey,
                        totalIncome: 0,
                        totalNights: 0,
                        totalExpenses: 0,
                        totalReservations: 0,
                    };
                }

                // Calculate nights
                const start = new Date(r.start_date);
                const end = new Date(r.end_date);
                const diffTime = Math.abs(end - start);
                const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                monthlyData[monthKey].totalIncome += Number(r.income) || 0;
                monthlyData[monthKey].totalNights += nights;
                monthlyData[monthKey].totalReservations += 1;
            });

            // Process Expenses
            expenses.forEach(e => {
                if (!e.date) return;
                const date = new Date(e.date);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!monthlyData[monthKey]) {
                    monthlyData[monthKey] = {
                        month: monthKey,
                        totalIncome: 0,
                        totalNights: 0,
                        totalExpenses: 0,
                        totalReservations: 0,
                    };
                }

                monthlyData[monthKey].totalExpenses += Number(e.value) || 0;
            });

            // Convert to array and sort
            const statsArray = Object.values(monthlyData).map(item => ({
                ...item,
                netIncome: item.totalIncome - item.totalExpenses
            })).sort((a, b) => b.month.localeCompare(a.month));

            setStats(statsArray);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }, [token, API_URL]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading) return <div className="p-4">Loading...</div>;

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <h2 className="text-2xl font-bold mb-6">Analyse Mensuelle</h2>
            <div className="bg-white p-4 rounded shadow overflow-x-auto">
                <table className="w-full table-auto border-collapse border text-center">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="border px-4 py-2">Mois / Année</th>
                            <th className="border px-4 py-2 text-green-600">Total Income</th>
                            <th className="border px-4 py-2">Total Nuits</th>
                            <th className="border px-4 py-2">Total Réservations</th>
                            <th className="border px-4 py-2 text-red-600">Total Business Expenses</th>
                            <th className="border px-4 py-2 text-blue-600">Net Income</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.map((stat) => (
                            <tr key={stat.month} className="hover:bg-gray-50">
                                <td className="border px-4 py-2 font-medium">{stat.month}</td>
                                <td className="border px-4 py-2">{stat.totalIncome.toFixed(2)} MAD</td>
                                <td className="border px-4 py-2">{stat.totalNights}</td>
                                <td className="border px-4 py-2">{stat.totalReservations}</td>
                                <td className="border px-4 py-2">{stat.totalExpenses.toFixed(2)} MAD</td>
                                <td className={`border px-4 py-2 font-bold ${stat.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {stat.netIncome.toFixed(2)} MAD
                                </td>
                            </tr>
                        ))}
                        {stats.length === 0 && (
                            <tr>
                                <td colSpan="6" className="border px-4 py-4 text-gray-500">Aucune donnée disponible</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ResaAnalyse;
